import * as turf from '@turf/turf';
import { Marker, Polygon, Polyline } from '@react-google-maps/api';
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';

//STYLES
import { ContainerPoint } from '../../styles';

//APP
import { errorAlert } from '../../../../../../../../../../../../../../../../utils/alert';
import { encontrarIndexDoPoligono, rotatePoint } from '../../meta';

//COMPONENTS
import ModalConfirmar from '../modalConfirmar';


export type GridMalha = {
    id?: number
    center: LatLngTuple
    oculto: boolean
    idPolygon: number
    posicao?: number
    polygons: turf.Feature<turf.Polygon>
}

export type GridPolygon = {
    id: number
    polygon: turf.Feature<turf.Polygon>
}

interface PropsGrids {
    polygon: turf.Feature
    novoPonto: boolean
    moverPonto: boolean
    removerPonto: boolean
    selecionarPonto: boolean
    rotation?: number
    isSliding: boolean
    gridLevel: number
    moverVertical: number
    moverHorizontal: number
    tab_selecionado: string
    abaAtual: number
    grid: GridMalha[]
    setGrid: React.Dispatch<React.SetStateAction<GridMalha[]>>
    gridOriginal: GridMalha[]
    setGridOriginal: React.Dispatch<React.SetStateAction<GridMalha[]>>
    gridOriginalRotacao: GridMalha[]
    setGridOriginalRotacao: React.Dispatch<React.SetStateAction<GridMalha[]>>
    setTamanhoGridOriginal: React.Dispatch<React.SetStateAction<number>>
    abrirModal: boolean
    setAbrirModal: React.Dispatch<React.SetStateAction<boolean>>
    confirmado: boolean
    setConfirmado: React.Dispatch<React.SetStateAction<boolean>>
    pontosSelecionados: any[]
    setPontosSelecionados: React.Dispatch<React.SetStateAction<any[]>>
    mousePosition: [number, number] | null
    setMousePosition: React.Dispatch<React.SetStateAction<[number, number] | null>>
    ancora: { lat: number; lng: number } | null
    mapClickEvent?: { lat: number; lng: number; ts: number } | null
}

type LatLngTuple = [number, number]

/* ---------- AJUDA ---------- */
// Se vier MultiPolygon, pega o primeiro polígono
const obterPrimeiroPoligono = (feat: turf.Feature) =>
    feat.geometry.type === 'Polygon' ? feat : turf.flatten(feat).features[0]

// Converte coordenadas de malhaNova para Google Maps {lat, lng}[]
function converterCoordsParaGoogle(coordsLatLng: number[][]): google.maps.LatLngLiteral[] {
    return coordsLatLng.map(([lat, lng]) => ({ lat, lng }))
}

// Determina a cor do marcador com base no estado
function obterIconeMarcador(
    mode: string | undefined,
    label: string
): google.maps.Symbol | google.maps.Icon {
    let fillColor = '#ffffff'
    let strokeColor = '#000000'

    if (mode === 'ponto1') {
        fillColor = '#ff0000'
        strokeColor = '#8b0000'
    } else if (mode === 'mover') {
        fillColor = '#ffffff'
        strokeColor = '#0000ff'
    } else if (mode === 'remover') {
        fillColor = '#ffffff'
        strokeColor = '#ff0000'
    }

    return {
        path: google.maps.SymbolPath.CIRCLE,
        scale: 10,
        fillColor,
        fillOpacity: 1,
        strokeColor,
        strokeWeight: 2,
    }
}

/* ---------- COMPONENTE ---------- */
export const MalhaGrid: React.FC<PropsGrids> = ({
    polygon: raw,
    novoPonto,
    moverPonto,
    removerPonto,
    selecionarPonto,
    rotation = 0,
    isSliding,
    moverVertical = 0,
    moverHorizontal = 0,
    tab_selecionado,
    grid,
    setGrid,
    gridOriginal,
    setGridOriginal,
    gridOriginalRotacao,
    setGridOriginalRotacao,
    abrirModal,
    setAbrirModal,
    setConfirmado,
    pontosSelecionados,
    setPontosSelecionados,
    mousePosition,
    setMousePosition,
    ancora,
    mapClickEvent,
}) => {
    const polygon = useMemo(() => obterPrimeiroPoligono(raw), [raw])
    const [malhaNova, setMalhaNova] = useState([])

    // Cache do buffer do polígono — evita recalcular turf.buffer a cada render/effect
    const polygonBuffered = useMemo(() => {
        if (!polygon) return null
        return turf.buffer(polygon, 0, { units: 'meters' })
    }, [polygon])

    // Trata cliques no mapa repassados pelo pai via mapClickEvent
    useEffect(() => {
        if (!mapClickEvent) return
        const { lat, lng } = mapClickEvent
        const point = turf.point([lng, lat])
        const estaDentro = turf.booleanPointInPolygon(point, polygon)

        const indexDoPoligono = encontrarIndexDoPoligono(point, malhaNova)

        const pontoExistePolygon = grid.filter(
            (item) => item.idPolygon === malhaNova[indexDoPoligono]?.id
        )

        if (novoPonto && estaDentro && tab_selecionado === 'Pontos') {
            if (pontoExistePolygon[0]?.oculto === false) {
                errorAlert('Não pode adicionar um novo ponto, ponto já existente')
                return
            } else {
                // Posição do marcador = onde o usuário clicou (dentro do talhão)
                // Ordenação zig-zag usa turf.centerOfMass(polygons), então não afeta a ordem
                setGrid((prev) =>
                    prev.map((item) =>
                        item.idPolygon === pontoExistePolygon[0]?.idPolygon
                            ? { ...item, center: [lat, lng], oculto: false }
                            : item
                    )
                )
                setGridOriginalRotacao((prev) =>
                    prev.map((item) =>
                        item.idPolygon === pontoExistePolygon[0]?.idPolygon
                            ? { ...item, center: [lat, lng], oculto: false }
                            : item
                    )
                )
            }
        }
    }, [mapClickEvent])

    const remocaoDosPontos = useCallback((id: number) => {
        setGrid((prev) => prev.map((item) => (item?.id === id ? { ...item, oculto: true } : item)))
        setGridOriginalRotacao((prev) =>
            prev.map((item) => (item?.id === id ? { ...item, oculto: true } : item))
        )
    }, [])

    function movimentarPonto(newLat: number, newLng: number, id: number, idPolygon: number) {
        const point = turf.point([newLng, newLat])
        const isInside = turf.booleanPointInPolygon(point, polygon)

        if (!isInside) {
            errorAlert('Ponto movido para fora do talhão!')
            // Força re-render para o marcador voltar à posição original
            setGrid(prev => [...prev])
            return
        }

        const indexDoPoligono = encontrarIndexDoPoligono(point, malhaNova)
        if (malhaNova[indexDoPoligono]?.id !== idPolygon) {
            errorAlert('Ponto movido para fora da area da malha')
            setGrid(prev => [...prev])
            return
        }

        const updateCenter = (prev) =>
            prev.map((cell) =>
                cell.id !== id ? cell : { ...cell, center: [newLat, newLng] as LatLngTuple }
            )

        setGrid(updateCenter)
        setGridOriginal(updateCenter)
        setGridOriginalRotacao(updateCenter)
    }

    const selecionar = useCallback((ponto) => {
        setPontosSelecionados((prev) => {
            if (prev.length >= 2) return prev
            return [...prev, ponto]
        })
    }, [])

    function ordenarZigZagPorDirecao(
        grid,
        ponto1: [number, number],
        ponto2: [number, number],
        opcoes?: {
            forcarInicioEmPonto1?: boolean
            rotacao?: number
            ancora?: { lat: number; lng: number } | null
            moverVertical?: number
            moverHorizontal?: number
        }
    ) {
        const [x1, y1] = ponto1
        const [x2, y2] = ponto2

        const rot = opcoes?.rotacao ?? 0
        const anc = opcoes?.ancora
        const offLat = (opcoes?.moverVertical ?? 0) / 111320
        const offLng = anc
            ? (opcoes?.moverHorizontal ?? 0) / (111320 * Math.cos((anc.lng * Math.PI) / 180))
            : 0

        // Função auxiliar: desfaz posição + rotação para obter coordenada original do grid
        const desrotacionar = (latLng: [number, number]): [number, number] => {
            let [lat, lng] = latLng
            // 1. Desfaz offset de posição
            lat -= offLat
            lng -= offLng
            // 2. Desfaz rotação
            if (rot !== 0 && anc) {
                const [uLat, uLng] = rotatePoint([lat, lng], [anc.lat, anc.lng], -rot)
                return [uLat, uLng]
            }
            return [lat, lng]
        }

        // 1. Calcula os centros geométricos e os centros "des-rotacionados" de TODOS os pontos
        const pointsWithCenters = grid.map((ponto) => {
            const centroid = turf.centerOfMass(ponto.polygons).geometry.coordinates
            const [cLng, cLat] = centroid // turf retorna [lng, lat]
            // Centro original do grid (sem rotação/posição) para projeção
            const original = desrotacionar([cLat, cLng])
            return {
                ...ponto,
                _gridCenter: original,
            }
        })

        // 2. Encontra o ponto inicial e final usando coordenadas visuais (center)
        const startPoint = pointsWithCenters.find(
            (p) => Math.abs(p.center[0] - x1) <= 0.000001 && Math.abs(p.center[1] - y1) <= 0.000001
        )
        const endPoint = pointsWithCenters.find(
            (p) => Math.abs(p.center[0] - x2) <= 0.000001 && Math.abs(p.center[1] - y2) <= 0.000001
        )

        // 3. Define o vetor de direção usando os CENTROS ORIGINAIS (des-rotacionados)
        let dx = (endPoint ? endPoint._gridCenter[0] : x2) - (startPoint ? startPoint._gridCenter[0] : x1)
        let dy = (endPoint ? endPoint._gridCenter[1] : y2) - (startPoint ? startPoint._gridCenter[1] : y1)

        // Snap para eixos cardeais se estiver próximo (evita drift em distâncias longas) 
        const absDx = Math.abs(dx)
        const absDy = Math.abs(dy)
        if (absDx > 0 && absDy > 0) {
            if (absDy / absDx < 0.2) dy = 0
            else if (absDx / absDy < 0.2) dx = 0
        }

        const mag = Math.sqrt(dx * dx + dy * dy)
        const dirX = mag === 0 ? 1 : dx / mag
        const dirY = mag === 0 ? 0 : dy / mag
        const perpX = -dirY
        const perpY = dirX

        // Projeta cada ponto usando centros ORIGINAIS (des-rotacionados)
        const projetados = pointsWithCenters.map((ponto) => {
            const [cLat, cLng] = ponto._gridCenter

            const originX = startPoint ? startPoint._gridCenter[0] : x1
            const originY = startPoint ? startPoint._gridCenter[1] : y1

            const relX = cLat - originX
            const relY = cLng - originY

            const projPrincipal = relX * dirX + relY * dirY
            const projPerpendicular = relX * perpX + relY * perpY

            return {
                ...ponto,
                _proj: projPrincipal,
                _offset: projPerpendicular,
            }
        })

        // Calcula tolerância dinâmica baseada no espaçamento real do grid
        const sortedOffsets = projetados.map(p => p._offset).sort((a, b) => a - b)
        const gaps: number[] = []
        for (let i = 1; i < sortedOffsets.length; i++) {
            const gap = sortedOffsets[i] - sortedOffsets[i - 1]
            if (gap > 0.00001) gaps.push(gap)
        }
        gaps.sort((a, b) => a - b)

        let tolerancia = 0.0003
        if (gaps.length > 0) {
            const mediana = gaps[Math.floor(gaps.length / 2)]
            tolerancia = mediana * 0.45
        }

        // Agrupa os pontos por faixa usando offsets ordenados e detecção de gaps
        const sortedProjetados = [...projetados].sort((a, b) => a._offset - b._offset)
        const faixas: (typeof projetados)[][] = [[sortedProjetados[0]]]
        for (let i = 1; i < sortedProjetados.length; i++) {
            const diff = sortedProjetados[i]._offset - sortedProjetados[i - 1]._offset
            if (diff <= tolerancia) {
                faixas[faixas.length - 1].push(sortedProjetados[i])
            } else {
                faixas.push([sortedProjetados[i]])
            }
        }

        // Encontrar a faixa do ponto1 (usando p.center que é a coordenada visual do marcador)
        const indexFaixaPonto1 = faixas.findIndex((faixa) =>
            faixa.some(
                (p) =>
                    Math.abs(p.center[0] - x1) <= 0.000001 &&
                    Math.abs(p.center[1] - y1) <= 0.000001
            )
        )

        if (indexFaixaPonto1 === -1) {
            console.log("Ponto1 não encontrado.")
            return
        }

        // Reorganiza as faixas começando pela faixa do ponto1
        const faixaCentral = faixas[indexFaixaPonto1]
        const antes = faixas.slice(0, indexFaixaPonto1).reverse()
        const depois = faixas.slice(indexFaixaPonto1 + 1)
        const novaOrdemFaixas = [faixaCentral, ...depois, ...antes]

        const resultado: GridMalha[] = []
        let contador = 1

        novaOrdemFaixas.forEach((faixa, i) => {
            if (i === 0) {
                // Começa a partir do ponto1 na primeira faixa
                const idx = faixa.findIndex(
                    (p) =>
                        Math.abs(p.center[0] - x1) <= 0.000001 &&
                        Math.abs(p.center[1] - y1) <= 0.000001
                )
                const ordenada = faixa
                    .slice(idx)
                    .concat(faixa.slice(0, idx))
                    .sort((a, b) => a._proj - b._proj)
                ordenada.forEach((p) => {
                    p.posicao = contador++
                    resultado.push(p)
                })
            } else {
                faixa.sort((a, b) =>
                    i % 2 === 0 ? a._proj - b._proj : b._proj - a._proj
                )
                faixa.forEach((p) => {
                    p.posicao = contador++
                    resultado.push(p)
                })
            }
        })

        setGrid(resultado)
    }

    function verificarPosicao(posicao) {
        if (posicao === undefined) {
            return removerPonto ? 'X' : ''
        }

        return String(posicao)
    }

    function opcaoSelecionar(lat?: number, lng?: number) {
        if (
            pontosSelecionados[0]?.[0] === lat &&
            pontosSelecionados[0]?.[1] === lng &&
            pontosSelecionados[0]?.[0] !== undefined &&
            pontosSelecionados[0]?.[1] !== undefined
        ) {
            return 'ponto1'
        }

        if (moverPonto) {
            return 'mover'
        }

        if (removerPonto) {
            return 'remover'
        }

        return undefined
    }

    // Executa em tempo real — sem debounce para rotação/posição
    useEffect(() => {
        if (!ancora) return
        const ancoraLat = ancora.lat
        const ancoraLng = ancora.lng
        const offsetLat = moverVertical / 111320
        const offsetLng = moverHorizontal / (111320 * Math.cos((ancoraLng * Math.PI) / 180))

        let cells: GridMalha[] = gridOriginal.map((cell) => {
            const centerCoords = turf.centerOfMass(cell.polygons).geometry.coordinates
            const [lng, lat] = centerCoords

            const movedLat = lat + offsetLat
            const movedLng = lng + offsetLng

            const movedCoords = cell.polygons.geometry.coordinates[0].map(([lng, lat]) => {
                return [lng + offsetLng, lat + offsetLat]
            })

            const movedPolygon = turf.polygon([movedCoords])

            return {
                id: cell.id,
                center: [movedLat, movedLng],
                idPolygon: cell?.idPolygon,
                oculto: cell?.oculto,
                polygons: movedPolygon,
            }
        })

        if (rotation !== 0) {
            cells = cells.map((item) => {
                const rotatedCenter = rotatePoint(item?.center, [ancoraLat, ancoraLng], rotation)
                const rotatedCoords = item?.polygons.geometry.coordinates[0].map(([lng, lat]) => {
                    const [rotLat, rotLng] = rotatePoint([lat, lng], [ancoraLat, ancoraLng], rotation)
                    return [rotLng, rotLat]
                })
                const rotatedPolygon = turf.polygon([rotatedCoords])
                return {
                    ...item,
                    center: rotatedCenter,
                    polygons: rotatedPolygon,
                }
            })
        }

        cells = cells.map((item) => {
            const ponto = turf.point([item.center[1], item.center[0]])
            const dentro = turf.booleanPointInPolygon(ponto, polygon)

            return {
                ...item,
                oculto: !dentro,
            }
        })

        if (rotation === 0) {
            setGrid(gridOriginal)
        }

        setGrid(cells?.length === 0 ? gridOriginal : cells)
        setGridOriginalRotacao(cells?.length === 0 ? gridOriginal : cells)
    }, [rotation, moverHorizontal, moverVertical])

    // Calcula malhaNova com debounce — usa polygonBuffered cacheado
    useEffect(() => {
        if (!grid || !polygonBuffered) return
        const handler = setTimeout(() => {
            const sourceGrid = tab_selecionado === 'Ordenacao' ? gridOriginalRotacao : grid

            const novaMalha = sourceGrid
                .map((cell) => {
                    const intersected = turf.intersect(cell.polygons, polygonBuffered)

                    if (!intersected || !['Polygon', 'MultiPolygon'].includes(intersected.geometry.type)) {
                        return null
                    }

                    const coordsLatLng = intersected.geometry.coordinates[0].map(([lng, lat]) => [lat, lng])

                    return {
                        id: cell?.idPolygon,
                        polygon: cell?.polygons,
                        coordsLatLng,
                    }
                })
                .filter(Boolean)

            setMalhaNova(novaMalha)
        }, 500)

        return () => clearTimeout(handler)
    }, [grid, moverHorizontal, moverVertical, rotation, polygonBuffered, tab_selecionado, gridOriginalRotacao])

    useEffect(() => {
        if (pontosSelecionados.length === 2) {
            const pontosFiltradosTalhao = grid.filter(item => item?.oculto === false)
            ordenarZigZagPorDirecao(pontosFiltradosTalhao, pontosSelecionados[0], pontosSelecionados[1], {
                forcarInicioEmPonto1: true,
                rotacao: rotation,
                ancora,
                moverVertical,
                moverHorizontal,
            })
        }
    }, [pontosSelecionados])

    // Memoiza os pontos visíveis — evita .filter() a cada render
    const gridVisiveis = useMemo(() => grid.filter((item) => item?.oculto === false), [grid])

    // Cria lookup Map de malhaNova por idPolygon — evita turf.buffer+intersect no render
    const malhaNovaMap = useMemo(() => {
        const map = new Map<number, any>()
        malhaNova.forEach((cell) => {
            map.set(cell.id, cell)
        })
        return map
    }, [malhaNova])

    return (
        <ContainerPoint>
            {/* Linha tracejada entre ponto1 selecionado e posição do mouse */}
            {pontosSelecionados.length === 1 && mousePosition && (
                <Polyline
                    path={[
                        { lat: pontosSelecionados[0][0], lng: pontosSelecionados[0][1] },
                        { lat: mousePosition[0], lng: mousePosition[1] },
                    ]}
                    options={{
                        strokeColor: 'red',
                        strokeOpacity: 1,
                        strokeWeight: 2,
                        icons: [
                            {
                                icon: { path: 'M 0,-1 0,1', strokeOpacity: 1, scale: 2 },
                                offset: '0',
                                repeat: '10px',
                            },
                        ],

                    }}
                />
            )}

            {gridVisiveis.map(({ id, idPolygon, center, polygons, posicao }, i) => {
                if (!center) return null
                const [lat, lng] = center

                const mode = opcaoSelecionar(lat, lng)
                const labelText = verificarPosicao(posicao)

                const pointMarker = (
                    <Marker
                        key={`pt-${id}`}
                        position={{ lat, lng }}
                        icon={obterIconeMarcador(mode, labelText)}
                        label={
                            labelText
                                ? {
                                    text: labelText,
                                    fontSize: '10px',
                                    fontWeight: 'bold',
                                    color: mode === 'ponto1' ? '#ffffff' : mode === 'remover' ? '#ff0000' : mode === 'mover' ? '#0000ff' : '#000000',
                                }
                                : undefined
                        }
                        draggable={moverPonto}
                        onDragEnd={(e) => {
                            if (!moverPonto) return
                            const newLat = e.latLng.lat()
                            const newLng = e.latLng.lng()
                            movimentarPonto(newLat, newLng, id, idPolygon)
                        }}
                        onClick={() => {
                            if (removerPonto) {
                                remocaoDosPontos(id)
                            } else if (selecionarPonto) {
                                selecionar(center)
                            }
                        }}
                    />
                )

                // Usa coordenadas pré-calculadas do malhaNova em vez de turf.buffer+intersect inline
                if (isSliding === false && tab_selecionado === 'Mapa') {
                    const cachedCell = malhaNovaMap.get(idPolygon)

                    if (!cachedCell) {
                        // Célula não tem interseção válida com o talhão
                        return pointMarker
                    }

                    return (
                        <Fragment key={`frag-${id}`}>
                            <Polygon
                                paths={converterCoordsParaGoogle(cachedCell.coordsLatLng)}
                                options={{ strokeColor: '#000000', strokeWeight: 1, fillOpacity: 0, clickable: false }}
                            />
                            {pointMarker}
                        </Fragment>
                    )
                }

                return pointMarker
            })}

            {(tab_selecionado === 'Pontos' || tab_selecionado === 'Ordenacao') &&
                malhaNova.map((cell) => {
                    return (
                        <Polygon
                            key={cell.id}
                            paths={converterCoordsParaGoogle(cell.coordsLatLng)}
                            options={{
                                strokeColor: '#000000',
                                strokeWeight: 1,
                                fillOpacity: 0,
                                clickable: false,
                            }}
                        />
                    )
                })}

            <ModalConfirmar
                abrir={abrirModal}
                abaAtual={tab_selecionado}
                setAbrir={setAbrirModal}
                setConfirmado={setConfirmado}
            />
        </ContainerPoint>
    )
}

// Exporta o handler de clique e mouse move para o pai conectar ao GoogleMap
export { obterPrimeiroPoligono }
